import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { KIND_NAMES, ROLE_IDS, ROLE_INFO, editStatsBody, resetStatsBody, type GameKind, type ProfileDto, type RoleId } from '@mafia/core';
import { KindTabs } from '@/components/Kind';
import { api } from '@/lib/api';
import { useFormSubmit } from '@/lib/forms';
import { useGame } from '@/store/game';
import { Alert, Button, Field, Input, Modal } from '@/components/ui';

const FIELDS = [
  ['played', 'Игр'],
  ['won', 'Побед'],
  ['survived', 'Выжил'],
  ['points', 'Баллы'],
] as const;
type FieldKey = (typeof FIELDS)[number][0];
type Draft = Record<RoleId, Record<FieldKey, string>>;

function draftOf(profile: ProfileDto, kind: GameKind): Draft {
  const draft = {} as Draft;
  const byRole = profile.byKind?.[kind]?.byRole ?? [];
  for (const roleId of ROLE_IDS) {
    const s = byRole.find((r) => r.roleId === roleId);
    draft[roleId] = { played: String(s?.played ?? 0), won: String(s?.won ?? 0), survived: String(s?.survived ?? 0), points: String(s?.points ?? 0) };
  }
  return draft;
}

/** Пустое поле — 0; нечисловое уходит в zod как NaN и подсвечивается. */
const num = (v: string) => (v.trim() === '' ? 0 : Number(v));

export function EditStatsModal({ open, onClose, userId, profile }: { open: boolean; onClose(): void; userId: string; profile: ProfileDto }) {
  const qc = useQueryClient();
  const toast = useGame((s) => s.toast);
  const [kind, setKind] = useState<GameKind>('AMATEUR');
  const [draft, setDraft] = useState<Draft>(() => draftOf(profile, 'AMATEUR'));
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setDraft(draftOf(profile, kind));
      setReason('');
    }
  }, [open, profile, kind]);

  const { errors, formError, pending, handle } = useFormSubmit(editStatsBody, async (body) => {
    await api(`/api/admin/users/${userId}/stats`, { method: 'PUT', body });
    await qc.invalidateQueries({ queryKey: ['admin'] });
    await qc.invalidateQueries({ queryKey: ['profile', userId] });
    toast('Статистика сохранена', 'good');
    onClose();
  });

  const rows = ROLE_IDS.map((roleId) => ({
    roleId,
    played: num(draft[roleId].played),
    won: num(draft[roleId].won),
    survived: num(draft[roleId].survived),
    points: num(draft[roleId].points),
  }));
  const played = rows.reduce((a, r) => a + (r.played || 0), 0);
  const won = rows.reduce((a, r) => a + (r.won || 0), 0);
  const points = rows.reduce((a, r) => a + (r.points || 0), 0);
  const errorOf = (i: number, k: FieldKey) => errors[`byRole.${i}.${k}`];
  const rowErrors = ROLE_IDS.map((_, i) => FIELDS.map(([k]) => errorOf(i, k)).find(Boolean)).filter(Boolean);

  return (
    <Modal open={open} onClose={onClose} title="Статистика игрока">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handle({ kind, byRole: rows, ...(reason.trim() ? { reason: reason.trim() } : {}) });
        }}
      >
        {formError && <Alert>{formError}</Alert>}
        <div>
          <KindTabs value={kind} onChange={(k) => setKind(k)} />
          <p className="mt-1.5 text-xs text-zinc-500">Любительская и турнирная статистика (и рейтинг) хранятся отдельно.</p>
        </div>
        <div className="-mx-1 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-zinc-500">
                <th className="px-1 pb-2 text-left font-normal">Роль</th>
                {FIELDS.map(([k, label]) => (
                  <th key={k} className="px-1 pb-2 text-left font-normal">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROLE_IDS.map((roleId, i) => (
                <tr key={roleId}>
                  <td className="whitespace-nowrap px-1 py-1" title={ROLE_INFO[roleId].name}>
                    {ROLE_INFO[roleId].emoji} <span className="hidden sm:inline">{ROLE_INFO[roleId].name}</span>
                  </td>
                  {FIELDS.map(([k, label]) => (
                    <td key={k} className="px-1 py-1">
                      <Input
                        aria-label={`${ROLE_INFO[roleId].name}: ${label}`}
                        inputMode="numeric"
                        className="!px-2 !py-1.5 tabular-nums"
                        style={{ width: k === 'points' ? '4.75rem' : '3.75rem' }}
                        value={draft[roleId][k]}
                        error={errorOf(i, k)}
                        onChange={(e) => setDraft({ ...draft, [roleId]: { ...draft[roleId], [k]: e.target.value.replace(/[^\d-]/g, '') } })}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rowErrors.length > 0 && <p className="text-sm text-blood-400">{rowErrors[0]}</p>}
        <p className="text-sm text-zinc-400">
          {KIND_NAMES[kind]} статистика — итого: {played} игр, {won} побед ({played ? Math.round((won / played) * 1000) / 10 : 0}%), {points} баллов. История матчей не меняется.
        </p>
        <Field label="Причина (необязательно)" hint="Попадёт в историю модерации вместе со списком изменений.">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </Field>
        <Button type="submit" variant="gold" className="w-full" loading={pending}>
          Сохранить
        </Button>
      </form>
    </Modal>
  );
}

export function ResetStatsModal({ open, onClose, userId, nickname }: { open: boolean; onClose(): void; userId: string; nickname: string }) {
  const qc = useQueryClient();
  const toast = useGame((s) => s.toast);
  const [reason, setReason] = useState('');
  const [kind, setKind] = useState<GameKind | 'ALL'>('ALL');
  const { formError, pending, handle } = useFormSubmit(resetStatsBody, async (body) => {
    await api(`/api/admin/users/${userId}/stats/reset`, { method: 'POST', body });
    await qc.invalidateQueries({ queryKey: ['admin'] });
    await qc.invalidateQueries({ queryKey: ['profile', userId] });
    toast('Статистика обнулена', 'good');
    setReason('');
    onClose();
  });

  return (
    <Modal open={open} onClose={onClose} title="Обнулить статистику?">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handle({ ...(kind === 'ALL' ? {} : { kind }), ...(reason.trim() ? { reason: reason.trim() } : {}) });
        }}
      >
        {formError && <Alert>{formError}</Alert>}
        <KindTabs value={kind} onChange={(k) => setKind(k)} withAll />
        <p className="text-sm text-zinc-400">
          У игрока {nickname} обнулятся игры, победы, винрейт и баллы
          {kind === 'ALL' ? ' всех видов игр' : kind === 'TOURNAMENT' ? ' в турнирных играх' : ' в любительских играх'} — он пропадёт из
          {kind === 'ALL' ? ' обоих рейтингов' : ' этого рейтинга'} до следующей партии. История матчей останется.
        </p>
        <Field label="Причина (необязательно)">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} autoFocus />
        </Field>
        <Button type="submit" variant="danger" className="w-full" loading={pending}>
          Обнулить
        </Button>
      </form>
    </Modal>
  );
}
